import { FC } from "react";
import { ChevronDownIcon, ChevronUpIcon, MinusIcon } from "@radix-ui/react-icons";
import { cn } from "@/lib/shadcn/utils";

interface ScoreChangeTextProps {
    value?: number;
    extraClasses?: string;
}

// A sibling of PercentageChangeText — NOT a variant of it. Three deliberate
// divergences (airbnb-review-performance-table SC-005/SC-006):
//   1. A real zero renders a visible "0.00" (PercentageChangeText hides it).
//   2. A non-finite / undefined value renders the unavailable em-dash "—",
//      never a real "0" (PercentageChangeText coerces non-finite to 0).
//   3. Values are review-score points on a 1–5 scale; there is never a "%".
const UNAVAILABLE = "—";

const ScoreChangeText: FC<ScoreChangeTextProps> = ({ value, extraClasses }) => {
    // A drift with no comparison basis is unavailable, not zero.
    if (value === undefined || !Number.isFinite(value)) {
        return (
            <div className="flex items-center justify-end gap-0.5">
                <span
                    data-testid="score-change-value"
                    className={cn("text-xs text-muted-foreground", extraClasses)}
                >
                    {UNAVAILABLE}
                </span>
            </div>
        );
    }

    const direction = value > 0 ? "up" : value < 0 ? "down" : "flat";

    // green-700/red-600 for WCAG AA (4.5:1) on white 12px text: green-700 ≈ 5.02:1,
    // red-600 ≈ 4.83:1. NOT the -500s (green-500 ~2.28:1, red-500 ~3.9:1) and NOT
    // green-600 (~3.30:1 — clears the 3:1 icon bar but fails 4.5:1 for text).
    const colorClasses =
        direction === "up" ? "text-green-700" : direction === "down" ? "text-red-600" : "text-gray-700";

    const iconClasses = cn("h-3 w-3", colorClasses);
    const icon =
        direction === "up" ? (
            <ChevronUpIcon data-direction="up" className={iconClasses} />
        ) : direction === "down" ? (
            <ChevronDownIcon data-direction="down" className={iconClasses} />
        ) : (
            <MinusIcon data-direction="flat" className={iconClasses} />
        );

    // Signed, 2-decimal points. toFixed carries the "-" for negatives; a "+" is
    // prepended for positives. A real zero renders the unsigned, visible "0.00".
    const magnitude = value.toFixed(2);
    const text = direction === "up" ? `+${magnitude}` : magnitude;

    return (
        <div className="flex items-center justify-end gap-0.5">
            {icon}
            <span data-testid="score-change-value" className={cn("text-xs", colorClasses, extraClasses)}>
                {text}
            </span>
        </div>
    );
};

export default ScoreChangeText;
